import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { BsPeopleFill, BsFileEarmarkCheckFill } from 'react-icons/bs';
import { FaMoneyBillWaveAlt } from 'react-icons/fa';
import BoltIcon from '@mui/icons-material/Bolt';
import TFBar from '../../Charts/Bar';
import { monthsShort } from '../../../common/utils';
import ToDateNumbers from '../../Charts/ToDateNumbers';
import { CommsPanelCtx } from '../../../contexts';


const emptyYoY = () => {
    const now = new Date();
    const thisYear = now.getFullYear();
    const lastYear = thisYear - 1;

    let yoy = [];
    monthsShort.forEach( m => {
        let obj = { name: m };
        obj[lastYear] = 0;
        obj[thisYear] = 0;

        yoy.push( { ...obj } );
    } );

    return ( yoy );
}

const YoYFlows = ( { aid, uid, viewing, title, opps, loans } ) => {
    const [ leadChartData, setLeadChartData ] = useState( emptyYoY() );
    const [ leadToDateNumbers, setLeadToDateNumbers ] = useState( [] );
    const [ paChartData, setPAChartData ] = useState( emptyYoY() );
    const [ paToDateNumbers, setPAToDateNumbers ] = useState( [] );
    const [ fundedChartData, setFundedChartData ] = useState( emptyYoY() );
    const [ fundedToDateNumbers, setFundedToDateNumbers ] = useState( [] );
    
    const { addPanel, closePanel } = useContext( CommsPanelCtx );

    useEffect( () => {
        let oc = {};
        let lc = {};

        if ( opps ) {
            Object.keys( opps ).forEach( loid => {
                const f = opps[loid];
                if ( !oc[f.aid] ) {
                    oc[f.aid] = 0;
                }
                oc[f.aid]++;
            } );
        }
        if ( loans ) {
            Object.keys( loans ).forEach( lid => {
                const f = loans[lid];
                if ( !lc[f.aid] ) {
                    lc[f.aid] = 0;
                }
                lc[f.aid]++;
            } );
        }
    }, [ opps, loans ] );

    useEffect( () => {
        let yoy = emptyYoY();
        let t = 0;
        let wtd = 0;
        let mtd = 0;
        let ytd = 0;

        if ( opps ) {
            const today = new Date();
            today.setHours( 12, 0, 0, 0 );

            Object.keys( opps ).forEach( loid => {
                const o = opps[loid];

                if ( viewing.includes( o.aid ) ) {
                    if ( o.oppDate ) {
                        const od = new Date( o.oppDate.match( 'T' ) ? o.oppDate : o.oppDate + 'T12:00:00.000' );
                        od.setHours( 12, 0, 0, 0 );

                        if ( od.getFullYear() >= ( today.getFullYear() - 1 ) ) {
                            const m = yoy.find( f => f.name === monthsShort[od.getMonth()] );
                            if ( m ) {
                                m[od.getFullYear()]++;
                            }
                        }

                        if ( od.getFullYear() === today.getFullYear() ) {
                            ytd++;

                            if ( od.getMonth() === today.getMonth() ) {
                                mtd++;

                                if ( od.getDate() === today.getDate() ) {
                                    t++;
                                }
                            }
                        }

                        // have to calc wtd outside of above logic since a week can span a month or year
                        const dow = today.getDay();
                        if ( dow > 0 ) {
                            const start = 1000 * 60 * 60 * 24 * dow;
                            if ( od.getTime() >= today.getTime() - start ) {
                                wtd++;
                            }
                        }
                    }
                }
            } );
        }

        console.log( 'yoy', yoy );
        setLeadChartData( yoy );
        setLeadToDateNumbers( [
            { label: 'Today', value: t },
            { label: 'This Week', value: wtd },
            { label: 'This Month', value: mtd },
            { label: 'This Year', value: ytd }
        ] );
    }, [ opps, aid, uid, viewing ] );

    useEffect( () => {
        let paYoy = emptyYoY();
        let pt = 0;
        let pwtd = 0;
        let pmtd = 0;
        let pytd = 0;
        let fundYoy = emptyYoY();
        let ft = 0;
        let fwtd = 0;
        let fmtd = 0;
        let fytd = 0;

        if ( loans ) {
            const today = new Date();
            today.setHours( 12, 0, 0, 0 );

            Object.keys( loans ).forEach( lid => {
                const l = loans[lid];

                if ( viewing.includes( l.aid ) ) {
                    // Pre-Approvals
                    if ( l.paDate ) {
                        const pad = new Date( l.paDate.match( 'T' ) ? l.paDate : l.paDate + 'T12:00:00.000' );
                        pad.setHours( 12, 0, 0, 0 );

                        if ( pad.getFullYear() >= ( today.getFullYear() - 1 ) ) {
                            const m = paYoy.find( f => f.name === monthsShort[pad.getMonth()] );
                            if ( m ) {
                                m[pad.getFullYear()]++;
                            }
                        }

                        if ( pad.getFullYear() === today.getFullYear() ) {
                            pytd++;

                            if ( pad.getMonth() === today.getMonth() ) {
                                pmtd++;

                                if ( pad.getDate() === today.getDate() ) {
                                    pt++;
                                }
                            }
                        }

                        // have to calc wtd outside of above logic since a week can span a month or year
                        const dow = today.getDay();
                        if ( dow > 0 ) {
                            const start = 1000 * 60 * 60 * 24 * dow;
                            if ( pad.getTime() >= today.getTime() - start ) {
                                pwtd++;
                            }
                        }
                    }

                    // Fundings
                    if ( [ 'Funded', 'Closed' ].includes( l.status ) && l.fundedDate ) {
                        const fd = new Date( l.fundedDate.match( 'T' ) ? l.fundedDate : l.fundedDate + 'T12:00:00.000' );
                        fd.setHours( 12, 0, 0, 0 );
                        
                        if ( fd.getFullYear() >= ( today.getFullYear() - 1 ) ) {
                            const m = fundYoy.find( f => f.name === monthsShort[fd.getMonth()] );
                            if ( m ) {
                                m[fd.getFullYear()]++;
                            }
                        }

                        if ( fd.getFullYear() === today.getFullYear() ) {
                            fytd++;

                            if ( fd.getMonth() === today.getMonth() ) {
                                fmtd++;

                                if ( fd.getDate() === today.getDate() ) {
                                    ft++;
                                }
                            }
                        }

                        // have to calc wtd outside of above logic since a week can span a month or year
                        const dow = today.getDay();
                        if ( dow > 0 ) {
                            const start = 1000 * 60 * 60 * 24 * dow;
                            if ( fd.getTime() >= today.getTime() - start ) {
                                fwtd++;
                            }
                        }
                    }
                }
            } );
        }

        console.log( 'paYoy', paYoy );
        setPAChartData( paYoy );
        setPAToDateNumbers( [
            { label: 'Today', value: pt },
            { label: 'This Week', value: pwtd },
            { label: 'This Month', value: pmtd },
            { label: 'This Year', value: pytd }
        ] );

        console.log( 'fundYoy', fundYoy );
        setFundedChartData( fundYoy );
        setFundedToDateNumbers( [
            { label: 'Today', value: ft },
            { label: 'This Week', value: fwtd },
            { label: 'This Month', value: fmtd },
            { label: 'This Year', value: fytd }
        ] );
    }, [ aid, uid, viewing, loans ] );
    
    
    return (
        <Row className='tf-section-container mt-0'>
            <Col>
                <Row>
                    <Col xs={4}>
                        <Row className='tf-dashboard-section-header mx-2'>
                            <Col className='p-0 ps-2 tf-dashboard-section-header-title'>
                                <BsPeopleFill />
                                <span className='ps-2'>Leads</span>
                            </Col>
                        </Row>
                        {/* <Row className='px-3'>
                            <Col className='px-3 pt-3 w-100'>
                                <Button variant='link' className="p-0 insight-button" onClick={ () => addPanel( { panelType: 'leo' } ) }><BoltIcon /></Button>
                                Overall leads down 11% vs trailing 45-day avg. <Link to='/contact/con_0vU3nyIGSvXOZ1XUAOCFe'>Bruce Bochy</Link> relationship at risk.
                            </Col>
                        </Row> */}
                        <Row className='px-3'>
                            <Col className='p-3 w-100'>
                                <ToDateNumbers data={leadToDateNumbers} />
                            </Col>
                        </Row>
                        <Row className='px-3'>
                            <Col className='p-3 w-100'>
                                <TFBar data={leadChartData} h={225} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={4}>
                        <Row className='tf-dashboard-section-header mx-2'>
                            <Col className='p-0 ps-2 tf-dashboard-section-header-title'>
                                <BsFileEarmarkCheckFill />
                                <span className='ps-2'>Pre-Approvals</span>
                            </Col>
                        </Row>
                        {/* <Row className='px-3'>
                            <Col className='px-3 pt-3 w-100'>
                                <Button variant='link' className="p-0 insight-button"><BoltIcon /></Button>
                                Prospect to Pre-Approval conversion up 4% - September campaign changes are working.
                            </Col>
                        </Row> */}
                        <Row className='px-3'>
                            <Col className='p-3 w-100'>
                                <ToDateNumbers data={paToDateNumbers} />
                            </Col>
                        </Row>
                        <Row className='px-3'>
                            <Col className='p-3'>
                                <TFBar data={paChartData} h={225} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={4}>
                        <Row className='tf-dashboard-section-header mx-2'>
                            <Col className='p-0 ps-2 tf-dashboard-section-header-title'>
                                <FaMoneyBillWaveAlt />
                                <span className='ps-2'>Fundings</span>
                            </Col>
                        </Row>
                        {/* <Row className='px-3'>
                            <Col className='px-3 pt-3 w-100'>
                                <Button variant='link' className="p-0 insight-button"><BoltIcon /></Button>
                                Pacing 7 fundings short of annual goal. Consider adjusting refi alerts to create more opportunities.
                            </Col>
                        </Row> */}
                        <Row className='px-3'>
                            <Col className='p-3 w-100'>
                                <ToDateNumbers data={fundedToDateNumbers} />
                            </Col>
                        </Row>
                        <Row className='px-3'>
                            <Col className='p-3'>
                                <TFBar data={fundedChartData} h={225} />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
        </Row>  
    )
}

const mapProps = ( state ) => ( {
    aid: state.user.aid,
    uid: state.user.email,
    viewing: state.user.viewing,
    opps: state.leadopps.byId,
    loans: state.loans.byId
} );
  
const mapDispatch = ( dispatch ) => ( {
} );
  
export default connect( mapProps, mapDispatch )( YoYFlows );